import { Search } from "lucide-react";
import { PageHero, CtaBlock, FaqList, Btn } from "@/components/site/Ui";
import { Reveal } from "@/components/site/Motion";
import Split, { Bullets } from "@/components/site/Split";
import { faq } from "@/content/site";
import { useSeo } from "@/lib/seo";

const searches = ["Restaurant Toulouse", "Institut de beauté Montauban", "Coiffeur Blagnac", "Spa Toulouse"];
const compare = [
  { t: "Réseaux sociaux", pros: "Montrer le quotidien, fidéliser.", cons: "Peu trouvés sur Google, algorithme non maîtrisé." },
  { t: "Plateformes", pros: "Visibles auprès d'un public déjà présent.", cons: "Vous y côtoyez vos concurrents, souvent avec commission." },
  { t: "Votre site", pros: "Vous maîtrisez le message et la réservation. Il peut ressortir sur les recherches locales.", cons: "Demande une conception soignée.", own: true },
];
const faqLd = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function SeoLocal() {
  useSeo({
    title: "Référencement local à Toulouse — être trouvé sur Google",
    description: "Référencement local à Toulouse : fiche Google Business Profile, site internet, bonnes pratiques SEO et indicateurs pour être trouvé par les clients du quartier.",
    path: "/referencement-local-toulouse", jsonLd: faqLd,
  });
  return (
    <>
      <PageHero testid="seo-hero" label="Référencement local — Toulouse" lines={["Être trouvé par ceux", "qui vous cherchent déjà."]}
        intro="Quand quelqu'un cherche un commerce près de chez lui, Google affiche une carte et quelques établissements. Mon travail : que vous y ayez toutes vos chances." />
      <section className="wrap pb-16" data-testid="seo-searches">
        <p className="label mb-5">Exemples de recherches locales</p>
        <div className="grid gap-3 md:grid-cols-2">
          {searches.map((s, i) => (
            <Reveal key={s} delay={i * 0.06}>
              <div className="flex items-center gap-4 border border-ink/20 bg-white/70 px-5 py-4">
                <Search className="h-5 w-5 text-accent" aria-hidden="true" />
                <span className="text-[17px] font-medium">« {s} »</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <Split label="01" title="Comment Google choisit." testid="seo-how">
        <p>Selon Google, trois critères comptent : la <strong className="text-ink">pertinence</strong>, la <strong className="text-ink">distance</strong> et la <strong className="text-ink">notoriété</strong>. Une position ne s'achète pas et personne ne peut garantir la première place. On peut rendre votre activité plus claire et plus cohérente.</p>
      </Split>
      <Split id="google-business-profile" label="02" title="Votre fiche Google Business Profile." testid="seo-gbp">
        <p>Horaires, adresse, téléphone, photos, avis, lien vers le site : c'est souvent le premier contact avec vous.</p>
        <Bullets items={["Bonnes catégories", "Description claire", "Services et horaires à jour", "Photos de qualité", "Coordonnées identiques au site", "Liens vers la réservation"]} />
      </Split>
      <Split label="03" title="Le rôle de votre site.">
        <p>La fiche renvoie vers le site. Le site confirme ce que vous faites, où et pour qui, avec une page par service.</p>
      </Split>
      <section className="wrap border-t border-ink/20 py-14 md:py-20" data-testid="seo-compare">
        <p className="label mb-4">04</p>
        <h2 className="display h-md mb-10 max-w-3xl md:text-[2.6rem]">Réseaux, plateformes, site : trois rôles.</h2>
        <div className="grid gap-px bg-ink/20 md:grid-cols-3">
          {compare.map((c) => (
            <div key={c.t} className={`p-7 ${c.own ? "on-dark bg-ink text-ivory" : "bg-ivory"}`}>
              <h3 className="display text-[1.9rem]">{c.t}</h3>
              <p className={`mt-4 text-[15px] leading-relaxed ${c.own ? "text-ivory" : "text-ink"}`}><span className="font-semibold">Atout — </span>{c.pros}</p>
              <p className={`mt-2 text-[15px] leading-relaxed ${c.own ? "text-[#CDD2CD]" : "text-mute"}`}><span className="font-semibold">Limite — </span>{c.cons}</p>
            </div>
          ))}
        </div>
      </section>
      <Split label="05" title="Les bonnes pratiques que j'applique.">
        <Bullets items={["Une page par service, un titre unique", "Des méta-descriptions écrites pour les gens", "Des titres H1, H2, H3 logiques", "Des images légères avec texte alternatif", "Des données structurées vérifiées", "Un site rapide sur mobile"]} />
      </Split>
      <Split label="06" title="Ce que l'on mesure.">
        <Bullets items={["Clics et impressions (Google Search Console)", "Appels et itinéraires depuis la fiche Google", "Formulaires envoyés", "Clics vers la réservation"]} />
        <p className="text-[15px]">Ces indicateurs montrent une tendance. Ce ne sont pas des promesses.</p>
      </Split>
      <section className="wrap border-t border-ink/20 py-14 md:py-20" data-testid="seo-faq">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="label mb-4">Questions fréquentes</p>
            <h2 className="display h-md md:text-[2.6rem]">Vos questions, mes réponses.</h2>
            <div className="mt-8"><Btn to="/diagnostic" track="cta_diagnostic" testid="seo-faq-cta">Demander un diagnostic</Btn></div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6"><FaqList items={faq} /></div>
        </div>
      </section>
      <CtaBlock />
    </>
  );
}
